import * as React from "react";
import { Box, Stack } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { ManualDeposit } from "../../types/finance";
import LoadingButton from "@mui/lab/LoadingButton";
import { useAddManualDepositMutation } from "../../services/finance.service";
import SelectReason from "../../components/SelectReason";
import SelectCompany from "../../components/SelectCompany";
import { BaseModal, StyledTextField, Button } from "../../components/global";

interface ModalManualDepositFormProps {
  open: boolean;
  onClose: () => void;
  data: ManualDeposit | null;
}

const ModalManualDepositForm = ({
  open,
  onClose,
  data,
}: ModalManualDepositFormProps) => {
  const [add, { isLoading: adding }] = useAddManualDepositMutation();

  const {
    handleSubmit,
    clearErrors,
    control,
    setValue,
    formState: { errors },
  } = useForm();

  const onSubmit = async (payload: any) => {
    try {
      await add(payload).unwrap();
      onClose();
    } catch (err) {
      onClose();
    }
  };

  React.useEffect(() => {
    if (open) {
      setValue("reason", data?.reason?.id || null);
      setValue("company", data?.company?.id || null);
      setValue("amount", data?.amount || 100);
      setValue("notes", data?.notes || "");

      clearErrors();
    }
  }, [data, open, setValue, clearErrors]);

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={`${!data ? "Add" : "Edit"} Manual Deposit`}
      maxWidth="xs"
      useGlassCard={false}
      footer={
        <>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={adding}
            onClick={handleSubmit(onSubmit)}
          >
            {!data ? "Create" : "Update"}
          </LoadingButton>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Controller
              name="reason"
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, value } }) => (
                <SelectReason
                  onChange={onChange}
                  value={value || null}
                  error={!!errors.reason}
                  helperText={!!errors.reason && "This field is required"}
                />
              )}
            />
            <Controller
              name="company"
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, value } }) => (
                <SelectCompany
                  onChange={onChange}
                  value={value || null}
                  error={!!errors.company}
                  helperText={!!errors.company && "This field is required"}
                />
              )}
            />
            <Controller
              name="amount"
              control={control}
              rules={{ required: true, min: 100 }}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <StyledTextField
                  label="Amount (min: 100)"
                  value={value || ""}
                  type="number"
                  fullWidth
                  onChange={onChange}
                  onBlur={onBlur}
                  inputRef={ref}
                  error={!!errors.amount}
                  helperText={!!errors.amount && "This field is required"}
                />
              )}
            />
            <Controller
              name="notes"
              control={control}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <StyledTextField
                  label="Notes"
                  value={value || ""}
                  fullWidth
                  onChange={onChange}
                  onBlur={onBlur}
                  inputRef={ref}
                  multiline
                  rows={3}
                />
              )}
            />
          </Stack>
          </Box>
      </form>
    </BaseModal>
  );
};
export default ModalManualDepositForm;
